import { ApplicationStateEnumLabels } from "@boundless/bl-api-client/lender/models/ApplicationStateEnumLabels"
import { ApplicationStatusEnumLabels } from "@boundless/bl-api-client/lender/models/ApplicationStatusEnumLabels"
import { Link } from "@tanstack/react-router"
import { createColumnHelper } from "@tanstack/react-table"
import { ArrowUpDownIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { formatDate } from "@/lib/utils"

import type { Application } from "@boundless/bl-api-client/lender/models"
import type { Column, Row } from "@tanstack/react-table"
const columnHelper = createColumnHelper<Application>()

export const applicationColumns = [
  columnHelper.accessor("companyName", {
    header: ({ column }: { column: Column<Application, unknown> }) => {
      return (
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            className="h-5 w-5 min-w-5"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="h-4 w-4" />
          </Button>
          <p className="text-center">Company</p>
        </div>
      )
    },
    cell: ({ row }: { row: Row<Application> }) => {
      return <div className="font-small">{row.getValue("companyName")}</div>
    },
    filterFn: (
      row: Row<Application>,
      columnId: string,
      filterValue: unknown,
    ) => {
      return row.getValue(columnId) === filterValue
    },
  }),
  columnHelper.accessor("lastModifiedAt", {
    header: ({ column }: { column: Column<Application, unknown> }) => {
      return (
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            className="h-5 w-5 min-w-5"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="h-4 w-4" />
          </Button>
          <p className="text-center">Last Modified</p>
        </div>
      )
    },
    cell: ({ row }: { row: Row<Application> }) => {
      return (
        <div className="font-small">
          {formatDate(row.getValue("lastModifiedAt"))}
        </div>
      )
    },
    filterFn: (
      row: Row<Application>,
      columnId: string,
      filterValue: unknown,
    ) => {
      const { from, to } = filterValue as {
        from: Date | null
        to: Date | null
      }
      const raw = row.getValue(columnId)
      const rowDate = raw ? new Date(raw as string) : null
      if (!rowDate) return false
      if (from && rowDate < from) return false
      if (to && rowDate > to) return false
      return true
    },
  }),
  columnHelper.accessor("submittedAt", {
    header: ({ column }: { column: Column<Application, unknown> }) => {
      return (
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            className="h-5 w-5 min-w-5"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="h-4 w-4" />
          </Button>
          <p className="text-center">Submitted</p>
        </div>
      )
    },
    cell: ({ row }: { row: Row<Application> }) => {
      return (
        <div className="font-small">
          {formatDate(row.getValue("submittedAt"))}
        </div>
      )
    },
    filterFn: (
      row: Row<Application>,
      columnId: string,
      filterValue: unknown,
    ) => {
      const { from, to } = filterValue as {
        from: Date | null
        to: Date | null
      }
      const raw = row.getValue(columnId)
      const rowDate = raw ? new Date(raw as string) : null
      if (!rowDate) return false
      if (from && rowDate < from) return false
      if (to && rowDate > to) return false
      return true
    },
  }),
  columnHelper.accessor("state", {
    header: ({ column }: { column: Column<Application, unknown> }) => {
      return (
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            className="h-5 w-5 min-w-5"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="h-4 w-4" />
          </Button>
          <p className="text-center">State</p>
        </div>
      )
    },
    cell: ({ row }: { row: Row<Application> }) => {
      return (
        <div className="font-small capitalize">
          {
            ApplicationStateEnumLabels[
              row.getValue("state") as keyof typeof ApplicationStateEnumLabels
            ]
          }
        </div>
      )
    },
    filterFn: (
      row: Row<Application>,
      columnId: string,
      filterValue: unknown,
    ) => {
      if (!filterValue) return true
      return row.getValue(columnId) === filterValue
    },
  }),
  columnHelper.accessor("status", {
    header: ({ column }: { column: Column<Application, unknown> }) => {
      return (
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            className="h-5 w-5 min-w-5"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="h-4 w-4" />
          </Button>
          <p className="text-center">Status</p>
        </div>
      )
    },
    cell: ({ row }: { row: Row<Application> }) => {
      const value = row.getValue("status") as string
      if (!value) return <div className="text-muted-foreground">&mdash;</div>
      return (
        <div className="font-small capitalize">
          {
            ApplicationStatusEnumLabels[
              value as keyof typeof ApplicationStatusEnumLabels
            ]
          }
        </div>
      )
    },
    filterFn: (
      row: Row<Application>,
      columnId: string,
      filterValue: unknown,
    ) => {
      if (!filterValue) return true
      return row.getValue(columnId) === filterValue
    },
  }),
  columnHelper.accessor("uuid", {
    header: "Details",
    cell: (info) => (
      <Link
        to={"/lender/applications/details/$uuid"}
        params={{ uuid: info.getValue() }}
        className="text-primary"
      >
        Details
      </Link>
    ),
  }),
]
